<template>
  <el-dialog
    :model-value="modelValue"
    :title="$t('locationGroups.title', { store: storeName })"
    width="min(760px, 95vw)"
    destroy-on-close
    @close="emit('update:modelValue', false)"
  >
    <el-alert
      v-if="notice"
      :type="notice === 'saved' ? 'success' : 'warning'"
      :closable="false"
      show-icon
      class="location-groups-manager__alert"
    >
      <template #title>{{ $t(`locationGroups.notice.${notice}`) }}</template>
      <el-button v-if="notice === 'savedReloadFailed'" link type="primary" @click="retryLoad">
        {{ $t('shared.retry') }}
      </el-button>
    </el-alert>

    <el-alert
      v-if="mutationError || (readError && notice !== 'savedReloadFailed')"
      type="error"
      :title="$t(errorTranslationKey)"
      :closable="false"
      show-icon
      class="location-groups-manager__alert"
    >
      <el-button v-if="readError && !mutationError" link type="primary" @click="retryLoad">
        {{ $t('shared.retry') }}
      </el-button>
    </el-alert>

    <LocationGroupEditor
      v-if="editorVisible"
      :key="editedGroup?.id ?? 'new'"
      :group="editedGroup"
      :submitting="isMutating"
      @cancel="closeEditor"
      @submit="save"
    />
    <template v-else>
      <div class="location-groups-manager__toolbar">
        <el-button type="primary" @click="openCreate">{{ $t('locationGroups.create') }}</el-button>
      </div>
      <div v-loading="isLoading">
        <LocationGroupsList :groups="groups" @edit="openEdit" @remove="remove" />
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { LocationGroupsRepository } from '../application/LocationGroupsRepository'
import { useLocationGroups } from '../composables/useLocationGroups'
import type { LocationGroup, LocationGroupDraft } from '../domain/locationGroup'
import LocationGroupEditor from './LocationGroupEditor.vue'
import LocationGroupsList from './LocationGroupsList.vue'

const props = defineProps<{
  modelValue: boolean
  storeId: string
  storeName: string
  repository: LocationGroupsRepository
  confirmDelete: (group: LocationGroup) => Promise<boolean>
}>()

const emit = defineEmits<{
  'update:modelValue': [visible: boolean]
}>()

const editorVisible = ref(false)
const editedGroup = ref<LocationGroup>()
const storeId = computed(() => (props.modelValue ? props.storeId : null))
const {
  groups,
  isLoading,
  isMutating,
  readError,
  mutationError,
  notice,
  load,
  retryLoad,
  create,
  update,
  remove: removeGroup,
  reset
} = useLocationGroups(props.repository, storeId)

const errorTranslationKey = computed(() => {
  const error = mutationError.value ?? readError.value
  const code = typeof error === 'object' && error && 'kind' in error ? String(error.kind) : 'unknown'
  return `locationGroups.errors.${code}`
})

watch(
  () => [props.modelValue, props.storeId] as const,
  ([visible], previous) => {
    const previousStoreId = previous?.[1]
    if (!visible) {
      reset()
      closeEditor()
    } else {
      if (previousStoreId && previousStoreId !== props.storeId) reset()
      void load()
    }
  },
  { immediate: true }
)

function openCreate(): void {
  editedGroup.value = undefined
  editorVisible.value = true
}

function openEdit(group: LocationGroup): void {
  editedGroup.value = group
  editorVisible.value = true
}

function closeEditor(): void {
  editorVisible.value = false
  editedGroup.value = undefined
}

async function save(draft: LocationGroupDraft): Promise<void> {
  const successful = editedGroup.value ? await update(editedGroup.value.id, draft) : await create(draft)
  if (successful) closeEditor()
}

async function remove(group: LocationGroup): Promise<void> {
  if (isMutating.value || !(await props.confirmDelete(group))) return
  await removeGroup(group.id)
}
</script>

<style scoped lang="scss">
.location-groups-manager {
  &__alert {
    margin-bottom: 16px;
  }

  &__toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }
}
</style>
